import { Ref, RefObject, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { getLocale } from "@/helpers/i18n"
import { cn } from "@/lib/utils"

import { ScrollArea } from "./scroll-area"

type Time = { hours: number; minutes: number }

type Props = {
  className?: string
  isDisabled?: boolean
  maxDate?: Date
  minDate?: Date
  onBlur?: VoidFunction
  onChange: (date: Date | null) => void
  onFocus?: VoidFunction
  placeholder?: string
  value: Date | null
}

/**
 * Combine the Calendar component with a home-made "time picker" component
 * to let users pick a date and time, used to schedule calls in the Call Report form.
 * Trigger onChange and close the popover only when a date AND time are selected.
 **/

export function DateAndTimePicker({
  className,
  isDisabled,
  maxDate,
  minDate,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  ref,
  value,
}: { ref?: Ref<HTMLButtonElement> } & Props) {
  const { t } = useTranslation()
  const locale = getLocale()

  const [draftDate, setDraftDate] = useState<Date | null>(null)
  const [draftTime, setDraftTime] = useState<Time | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const selectedDate = draftDate ?? value
  const selectedTime = draftTime ?? getDefaultTime(value)

  const clearDraft = () => {
    setDraftDate(null)
    setDraftTime(null)
  }

  const commit = (date: Date) => {
    onChange(date)
    clearDraft()
    setIsOpen(false)
  }

  const availableTimes = getAvailableTimes({
    maxDate,
    minDate,
    selectedDate,
  })

  const handleSelectDate = (date: Date | undefined) => {
    if (!date) return
    if (!selectedTime) {
      return setDraftDate(date)
    }
    date.setHours(selectedTime.hours, selectedTime.minutes)
    commit(date)
  }

  const handleSelectTime = (time: Time) => {
    if (!selectedDate) {
      return setDraftTime(time)
    }
    const newDate = new Date(selectedDate)
    newDate.setHours(time.hours, time.minutes)
    commit(newDate)
  }

  // The focus and blur events for the datetime picker component are handled by the popover content
  // component via onOpenAutoFocus and onCloseAutoFocus props. On focus we must clear any validation errors
  // and on blur we must validate the field.
  const onPopoverContentFocus = () => {
    if (onFocus) {
      onFocus()
    }
  }

  const onPopoverContentBlur = () => {
    if (onBlur) {
      onBlur()
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          disabled={isDisabled}
          variant={"outline"}
          className={cn(
            `
              w-[320px] justify-between border-input text-left
              disabled:bg-neutral-200 disabled:text-neutral-400
              disabled:opacity-100
            `,
            !value && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex flex-1 justify-start">
            {selectedDate
              ? format(selectedDate, "PPP", { locale }) +
                " " +
                formatTime(selectedTime)
              : placeholder || t("global.datepicker.placeholder")}
          </span>
          <CalendarIcon className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        onOpenAutoFocus={onPopoverContentFocus}
        onCloseAutoFocus={onPopoverContentBlur}
      >
        <div className="flex w-full gap-4">
          <Calendar
            locale={locale}
            mode="single"
            selected={selectedDate || undefined}
            showOutsideDays
            onSelect={handleSelectDate}
            initialFocus
            fromDate={minDate}
            toDate={maxDate}
          />
          <TimePicker
            availableTimes={availableTimes}
            onChange={handleSelectTime}
            value={selectedTime}
            isOpen={isOpen}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

function TimePicker({
  availableTimes,
  isOpen,
  onChange,
  value,
}: {
  availableTimes: Time[]
  isOpen: boolean
  onChange: (time: Time) => void
  value: Time | null
}) {
  const { t } = useTranslation()
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  useScrollToSelectedTime({ availableTimes, isOpen, scrollAreaRef, value })

  return (
    <div className="border-l">
      <div className="flex items-center justify-center border-b py-4 text-sm">
        {t("global.datepicker.timeLabel")}
      </div>
      <ScrollArea className="h-[300px]" ref={scrollAreaRef}>
        <div className="py-2 pr-4 pl-2">
          {availableTimes.map((time) => (
            <div
              key={time.hours + "/" + time.minutes}
              className={cn(
                "cursor-pointer rounded-sm px-8 py-1",
                value && isSameTime(time, value)
                  ? "bg-primary-400 text-neutral-100"
                  : "hover:bg-primary-100 hover:font-bold",
              )}
              onClick={() => onChange(time)}
            >
              {formatTime(time)}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

function getDefaultTime(value: Date | null): Time | null {
  if (!value) return null
  const hours = value.getHours()
  const minutes = value.getMinutes()

  return { hours, minutes: minutes >= 30 ? 30 : 0 }
}

function useScrollToSelectedTime({
  availableTimes,
  isOpen,
  scrollAreaRef,
  value,
}: {
  availableTimes: Time[]
  isOpen: boolean
  scrollAreaRef: RefObject<HTMLDivElement | null>
  value: Time | null
}) {
  const ITEM_HEIGHT = 32

  return useLayoutEffect(() => {
    if (isOpen && value && scrollAreaRef.current) {
      const selectedIndex = availableTimes.findIndex((time) =>
        isSameTime(time, value),
      )
      const scrollPosition = selectedIndex * ITEM_HEIGHT
      scrollAreaRef.current.scrollTo(0, scrollPosition)
    }
  })
}

function formatTime(time?: Time | null) {
  if (!time) return "--:--"

  return (
    time.hours.toString().padStart(2, "0") +
    ":" +
    time.minutes.toString().padStart(2, "0")
  )
}

function isSameTime(a: Time, b: Time) {
  return a.hours === b.hours && a.minutes === b.minutes
}

function getAvailableTimes({
  maxDate,
  minDate,
  selectedDate,
}: {
  selectedDate: Date | null
} & Pick<Props, "maxDate" | "minDate">) {
  const START_HOUR = 9
  const END_HOUR = 21

  const numSlots = (END_HOUR - START_HOUR) * 2 + 1

  const allTimes = Array.from({ length: numSlots }, (_, i) => {
    const hours = START_HOUR + Math.floor(i / 2)
    const minutes = i % 2 === 0 ? 0 : 30

    return { hours, minutes }
  })

  const timeDate = selectedDate ? new Date(selectedDate) : new Date()

  if (minDate && maxDate) {
    return allTimes.filter((time) => {
      timeDate.setHours(time.hours, time.minutes)

      return timeDate >= minDate && timeDate <= maxDate
    })
  }

  if (minDate) {
    return allTimes.filter((time) => {
      timeDate.setHours(time.hours, time.minutes)

      return timeDate >= minDate
    })
  }

  if (maxDate) {
    return allTimes.filter((time) => {
      timeDate.setHours(time.hours, time.minutes)

      return timeDate <= maxDate
    })
  }

  return allTimes
}
